import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronRight, Download, Flame, RefreshCw } from 'lucide-react';
import TickerTape from '../components/layout/TickerTape';
import OfferStrip from '../components/common/OfferStrip';
import AdSlot from '../components/advertisement/AdSlot';
import FaqSection from '../components/common/FaqSection';
import TopStatCards from '../components/top-subscribed/TopStatCards';
import TopDemandLeaderboard from '../components/top-subscribed/TopDemandLeaderboard';
import TopSubscribedTable from '../components/top-subscribed/TopSubscribedTable';
import { TOP_SUBSCRIBED_FAQS } from '../data/topSubscribedData';
import {
  TOP_TABS,
  buildTopSubscribedCsv,
  computeTopStats,
  filterTopRows,
  mapTopSubscribedRows,
  topTabByKey,
} from '../utils/topSubscribed';
import { formatRefreshedAt } from '../utils/gmpBoard';
import { todayIso } from '../utils/ipoCalendar';
import { fetchTopSubscribed } from '../store/slices/topSubscriptionSlice';
import {
  fetchSmeSubscriptionSummary,
  fetchSubscriptionSummary,
  selectSmeSubscriptionRows,
  selectSubscriptionRows,
} from '../store/slices/subscriptionSlice';

const DATA_MAX_AGE_MS = 60000;
const YEAR = String(new Date().getFullYear());
const HEADING = `Top Subscribed IPOs ${YEAR}`;

export const TopSubscribedPage = () => {
  const dispatch = useDispatch();
  const today = useMemo(() => todayIso(), []);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = topTabByKey(searchParams.get('tab')).key;
  const [band, setBand] = useState('all');
  const [query, setQuery] = useState('');
  const tableRef = useRef(null);

  // GET /api/market/subscription/top is the ranking; the live subscription
  // feeds add the NSE symbol, so a row can link to its bid book
  const items = useSelector((state) => state.topSubscription.items);
  const status = useSelector((state) => state.topSubscription.status);
  const error = useSelector((state) => state.topSubscription.error);
  const note = useSelector((state) => state.topSubscription.note);
  const refreshedAt = useSelector((state) => state.topSubscription.refreshedAt);
  const subscriptionRows = useSelector(selectSubscriptionRows);
  const smeSubscriptionRows = useSelector(selectSmeSubscriptionRows);

  const loadFeeds = (force = false) => {
    const options = force ? {} : { maxAgeMs: DATA_MAX_AGE_MS };
    dispatch(fetchTopSubscribed(options));
    dispatch(fetchSubscriptionSummary(options));
    dispatch(fetchSmeSubscriptionSummary(options));
  };

  useEffect(() => {
    loadFeeds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const ISSUES = useMemo(
    () => mapTopSubscribedRows(items, [...subscriptionRows, ...smeSubscriptionRows]),
    [items, subscriptionRows, smeSubscriptionRows]
  );
  const stats = useMemo(() => computeTopStats(ISSUES), [ISSUES]);
  const tabCounts = useMemo(
    () => Object.fromEntries(TOP_TABS.map((option) => [option.key, ISSUES.filter(option.test).length])),
    [ISSUES]
  );
  const rows = useMemo(() => filterTopRows(ISSUES, { tab, band, query }), [ISSUES, tab, band, query]);
  const boardRows = useMemo(() => ISSUES.filter(topTabByKey(tab).test), [ISSUES, tab]);

  const isLoading = !ISSUES.length && (status === 'idle' || status === 'loading');
  const hasFailed = !ISSUES.length && status === 'failed';
  const refreshedLabel = formatRefreshedAt(refreshedAt);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${HEADING}: Most Oversubscribed Mainboard & SME Issues | Alambrix IPO`;
    return () => {
      document.title = previousTitle;
    };
  }, []);

  const changeTab = (key) => setSearchParams(key === 'all' ? {} : { tab: key }, { replace: true });

  const clearFilters = () => {
    setBand('all');
    setQuery('');
  };

  const handleExport = () => {
    // BOM so spreadsheet apps read ₹ correctly
    const blob = new Blob(['﻿', buildTopSubscribedCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `top-subscribed-ipos-${tab}-${today}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  let emptyMessage;
  if (hasFailed) {
    emptyMessage = (
      <>
        The subscription ranking is temporarily unavailable{error ? ` (${error})` : ''}.{' '}
        <button type="button" onClick={() => loadFeeds(true)} className="font-bold text-[#008080] hover:underline">
          Retry
        </button>
      </>
    );
  } else if (!ISSUES.length) {
    emptyMessage = note || 'No IPO has recorded bidding figures yet. Issues appear here from day one of their bidding window.';
  } else if (band !== 'all' || query) {
    emptyMessage = (
      <>
        No issues match these filters.{' '}
        <button type="button" onClick={clearFilters} className="font-bold text-[#008080] hover:underline">
          Clear filters
        </button>
      </>
    );
  } else {
    emptyMessage = `No ${topTabByKey(tab).label.toLowerCase()} issues in the ranking yet.`;
  }

  return (
    <>
      <TickerTape />

      <main className="flex-1 bg-slate-50/70">
        {/* Partner ad (GET /api/ads): page slot first, then the home leaderboard */}
        <OfferStrip slotId={['top-subscribed-top', 'leaderboard']} />

        <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28 pt-5 sm:pt-6 pb-12 sm:pb-16 space-y-5 sm:space-y-6">
          <div>
            <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-slate-500 font-medium overflow-x-auto whitespace-nowrap">
              <Link to="/" className="hover:text-[#008080] transition-colors">
                Home
              </Link>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <Link to="/ipo-subscription" className="hover:text-[#008080] transition-colors">
                IPO Subscription
              </Link>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="text-slate-900 font-semibold" aria-current="page">
                Top Subscribed IPOs
              </span>
            </nav>

            <div className="mt-3 flex flex-col xl:flex-row xl:items-end justify-between gap-4">
              <div className="max-w-3xl">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight leading-tight">{HEADING}</h1>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-orange-200 bg-orange-50 text-[11px] font-bold text-orange-700">
                    <Flame className="w-3.5 h-3.5" aria-hidden="true" />
                    Demand Ranking
                  </span>
                </div>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                  Mainboard and SME issues ranked by how many times their public book was subscribed, with grey market
                  premium, issue size and the bidding window alongside.
                  {refreshedLabel && <span className="text-slate-400"> Updated {refreshedLabel}.</span>}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => loadFeeds(true)}
                  disabled={status === 'loading'}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:border-slate-300 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${status === 'loading' ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                <button
                  type="button"
                  onClick={handleExport}
                  disabled={!rows.length}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:border-slate-300 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  Export CSV
                </button>
              </div>
            </div>
          </div>

          <TopStatCards stats={stats} loading={isLoading} />

          {/* The leaderboard sits beside the table only from 1440px, where the table has room */}
          <div className="grid grid-cols-1 min-[1440px]:grid-cols-[minmax(0,1fr)_340px] gap-5 sm:gap-6 items-start">
            <div className="min-w-0">
              <TopSubscribedTable
                ref={tableRef}
                rows={rows}
                maxTimes={ISSUES[0]?.times || 0}
                tab={tab}
                onTabChange={changeTab}
                tabCounts={tabCounts}
                band={band}
                onBandChange={setBand}
                query={query}
                onQueryChange={setQuery}
                loading={isLoading}
                emptyMessage={emptyMessage}
              />
            </div>

            <div className="space-y-5 sm:space-y-6 min-w-0">
              <TopDemandLeaderboard rows={boardRows} loading={isLoading} />
              <AdSlot slotId={['top-subscribed-sidebar', 'incontent']} variant="compact" />
            </div>
          </div>

          <FaqSection
            id="top-subscribed-faq"
            eyebrow="Investor Education"
            title="Understanding IPO Subscription & Allotment Odds"
            subtitle="What the multiples mean, and what they do and don't tell you about listing day."
            faqs={TOP_SUBSCRIBED_FAQS}
          />
        </div>
      </main>
    </>
  );
};

export default TopSubscribedPage;
